import React, { useMemo, useState } from 'react';
import { Modal } from '@/components/Modals/modal';
import { ModalBody, ModalImpl } from '../ModalFactory';
import { Calendar, Clock, ChevronRight } from 'lucide-react';
import { addDays, startOfDay, format, parse } from 'date-fns';

type DatePickerData = {
  anchorRect: DOMRect;
  currentDate?: Date;
  onConfirm: (date: Date) => void;
};

export const DatePickerModal: ModalImpl = (props) => {
  return (
    <Modal
      variant="date-picker"
      ariaLabel="Select Pickup Time"
      className="!p-0" // Remove default padding for edge-to-edge design
      darkenOverlay={false} // Popovers shouldn't darken the background
      {...props}
    >
      {DatePickerBody}
    </Modal>
  );
};

const DatePickerBody: ModalBody<DatePickerData> = ({
  modalData,
  withModalClose,
}) => {
  // 1. Dynamically generate the 6-day rolling window (Day 2 through Day 7)
  const availableDays = useMemo(() => {
    const today = new Date();
    // Creates an array of 6 elements mapping to: +2, +3, +4, +5, +6, +7 days
    return Array.from({ length: 6 }).map((_, index) => {
      return startOfDay(addDays(today, index + 2));
    });
  }, []);

  // 2. State Management
  // Default to the first available day (index 0)
  const [selectedDayIdx, setSelectedDayIdx] = useState<number>(0);
  const [selectedTime, setSelectedTime] = useState<string>('');

  // 3. The specific times requested
  const times = [
    '10:00 am',
    '11:00 am',
    '12:00 pm',
    '1:00 pm',
    '2:00 pm',
    '3:00 pm',
    '4:00 pm',
  ];

  const handleConfirm = withModalClose(async () => {
    if (selectedTime) {
      // 4. Merge the selected Day and Time into a single, accurate Date object
      const baseDate = availableDays[selectedDayIdx];
      // Parses the string '10:00 am' and applies those exact hours/minutes to the baseDate
      const finalDateTime = parse(selectedTime, 'h:mm a', baseDate);

      modalData?.onConfirm(finalDateTime);
    }
  });

  return (
    <div className="flex flex-col w-full bg-cream text-text-main shadow-lift">
      {/* HEADER */}
      <div className="p-4 border-b border-brand-truffle/5 bg-brand-cream/50">
        <h3 className="font-display font-bold text-lg text-text-heading flex items-center gap-2">
          <Clock size={18} className="text-brand-truffle" />
          Schedule Pickup
        </h3>
        <p className="font-body text-xs text-text-muted mt-1">
          When would you like your order ready?
        </p>
      </div>

      {/* BODY - Split View */}
      <div className="flex flex-row h-[280px]">
        {/* Left Column - Days */}
        <div className="w-1/2 border-r border-brand-truffle/5 bg-gray-50/50 p-2 overflow-y-auto custom-scrollbar">
          {availableDays.map((dateObj, idx) => {
            // Format to a clean string like "Fri, May 1"
            const dayLabel = format(dateObj, 'EEE, MMM d');
            return (
              <button
                key={dayLabel}
                onClick={() => setSelectedDayIdx(idx)}
                className={`w-full text-left px-3 py-3 rounded-[var(--radius-artisan)] text-sm font-[500] transition-all mb-1 ${
                  selectedDayIdx === idx
                    ? 'bg-cream shadow-xs text-brand-truffle border border-brand-truffle/10'
                    : 'text-text-muted hover:bg-black/5 border border-transparent'
                }`}
              >
                {dayLabel}
              </button>
            );
          })}
        </div>

        {/* Right Column - Times */}
        <div className="w-1/2 p-2 overflow-y-auto custom-scrollbar bg-cream">
          {times.map((time) => (
            <button
              key={time}
              onClick={() => setSelectedTime(time)}
              className={`w-full text-left px-4 py-2.5 rounded-[var(--radius-artisan)] text-sm font-[500] transition-colors mb-1 flex justify-between items-center ${
                selectedTime === time
                  ? 'bg-brand-sage/10 text-brand-sage font-bold'
                  : 'text-text-main hover:bg-brand-frost-wash'
              }`}
            >
              {time}
              {selectedTime === time && (
                <div className="w-1.5 h-1.5 rounded-full bg-brand-sage" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* FOOTER */}
      <div className="w-full flex justify-center p-4 border-t border-brand-truffle/5 bg-cream">
        <button
          onClick={handleConfirm}
          disabled={!selectedTime}
          className="w-[50%] flex items-center justify-center gap-2 px-6 py-2 rounded-full bg-brand-burgundy/80 border-2 border-brand-burgundy/20 text-brand-cream font-display font-[500] text-[10px] tracking-widest uppercase transition-all hover:bg-brand-cream hover:text-brand-burgundy/80 hover:border-brand-burgundy/80 disabled:opacity-50 disabled:cursor-not-allowed lift-light"
        >
          <span className="inline-block translate-y-[1px]">Confirm</span>
        </button>
      </div>
    </div>
  );
};
